<template>
    <b-modal v-model="show" :title="title" centered ok-variant="danger" ok-title="Remove"
        :ok-disabled="required && !reason.trim()" @ok="onOk" @cancel="onCancel" @hide="onHide">
        <p v-if="itemLabel" class="mb-2">
            You are removing <strong>{{ itemLabel }}</strong> from the patient's profile.
        </p>
        <b-form-group :label="required ? 'Reason for removal (required)' : 'Reason for removal (optional)'"
            class="mb-0">
            <b-form-textarea v-model="reason" rows="3" maxlength="500"
                placeholder="Explain why this is being removed from the patient's profile" />
        </b-form-group>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

interface RemovalReasonPromptOptions {
    itemLabel?: string;
    required?: boolean;
    title?: string;
}

/**
 * Reusable modal that collects a free-text removal reason. Call prompt() and await the result:
 * resolves to the trimmed reason when confirmed, or null when the user cancels/dismisses.
 */
@Component({ name: 'RemovalReasonModal' })
export default class RemovalReasonModal extends Vue {
    private show = false;
    private title = 'Remove from Profile';
    private itemLabel = '';
    private required = false;
    private reason = '';
    private resolved = false;
    private resolver: ((value: string | null) => void) | null = null;

    prompt(options: RemovalReasonPromptOptions = {}): Promise<string | null> {
        this.itemLabel = options.itemLabel ?? '';
        this.required = options.required ?? false;
        this.title = options.title ?? 'Remove from Profile';
        this.reason = '';
        this.resolved = false;
        this.show = true;
        return new Promise<string | null>(resolve => { this.resolver = resolve; });
    }

    private onOk() {
        this.finish(this.reason.trim());
    }

    private onCancel() {
        this.finish(null);
    }

    private onHide() {
        // Backdrop click / ESC close where neither ok nor cancel fired.
        if (!this.resolved) this.finish(null);
    }

    private finish(value: string | null) {
        if (this.resolved) return;
        this.resolved = true;
        this.resolver?.(value);
        this.resolver = null;
    }
}
</script>
